import { useNavigate } from "react-router";

import { t } from "i18next";

import { Box, Button, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { PageContainer } from "@/src/components/atoms";

import { CONSUMER_HOMEPAGE_PREFIX } from "@/src/pages/consumer/HomePage/HomePageContent";
import { onAuctionListingClick } from "@/src/pages/consumer/HomePage/homePageUtils";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { VehicleType } from "@/src/types/types";
import type { DealerLogoData } from "@/src/utils/dealerLogoUtils";
import type { CityNameProps } from "@/src/utils/weBuyCarsLandingPages";

import DealerLogoCards from "./components/DealerLogoCards";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom } from "jotai";

interface WeBuyCarsPageProps {
  cityName?: CityNameProps;
  dealerLogos: DealerLogoData;
}

const WeBuyCars = ({ cityName, dealerLogos }: WeBuyCarsPageProps) => {
  const { isAuthenticated, user, login } = useAuth();
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const draftAuction = userAuctionsBadges?.draftAuctionId;
  const activeAuction = userAuctionsBadges?.activeAuctionId;

  const pageTitle = t(
    cityName?.basic
      ? "components.weBuyCarsPage.subtitleWithCity"
      : "components.weBuyCarsPage.subtitle",
    { cityName }
  );
  let buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.startAuction`;

  if (draftAuction) {
    buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.continueAuction`;
  }

  return (
    <PageContainer maxWidth="1200px" sx={{ margin: "auto", mt: 2 }}>
      <Typography
        variant="h1"
        sx={{
          pb: 3,
          fontWeight: theme.typography.fontWeightBold,
          textAlign: "center"
        }}
      >
        {t(
          cityName?.inessive && cityName?.genetive
            ? "components.weBuyCarsPage.titleWithCity"
            : "components.weBuyCarsPage.title",
          { cityName }
        )}
      </Typography>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsPage.subtitle")}
      </Typography>

      <Typography variant="body1" sx={{ pb: 3, textAlign: "center" }}>
        {t(
          cityName?.inessive
            ? "components.weBuyCarsPage.body1WithCity"
            : "components.weBuyCarsPage.body1",
          { cityName }
        )}
      </Typography>
      <Box sx={{ justifyContent: "center", display: "flex", m: 2 }}>
        <Button
          sx={{ mb: 3 }}
          variant="contained"
          onClick={() =>
            onAuctionListingClick(
              LoginSource.WE_BUY_CARS_PAGE_CTA1,
              isAuthenticated,
              draftAuction,
              activeAuction,
              navigate,
              user,
              sendDataToGTM,
              login,
              VehicleType.CAR
            )
          }
        >
          {t(buttonText)}
        </Button>
      </Box>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsPage.title2")}
      </Typography>
      <Typography variant="body1" sx={{ pb: 1, textAlign: "center" }}>
        {t("components.weBuyCarsPage.body2")}
      </Typography>
      <Typography variant="body1" sx={{ pb: 3, textAlign: "center" }}>
        {t(
          cityName?.inessive
            ? "components.weBuyCarsPage.body3WithCity"
            : "components.weBuyCarsPage.body3",
          { cityName }
        )}
      </Typography>
      <Typography variant="h2" sx={{ pb: 3, textAlign: "center" }}>
        {t("components.weBuyCarsPage.title3")}
      </Typography>
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        {t("components.weBuyCarsPage.body4")}
      </Typography>

      <Box sx={{ justifyContent: "center", display: "flex", m: 2 }}>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() =>
            onAuctionListingClick(
              LoginSource.WE_BUY_CARS_PAGE_CTA2,
              isAuthenticated,
              draftAuction,
              activeAuction,
              navigate,
              user,
              sendDataToGTM,
              login,
              VehicleType.CAR
            )
          }
        >
          {t(buttonText)}
        </Button>
      </Box>
      <Typography variant="h2" sx={{ p: 2, textAlign: "center" }}>
        {pageTitle}
      </Typography>
      <DealerLogoCards dealerLogos={dealerLogos} />
    </PageContainer>
  );
};

export default WeBuyCars;
